import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Paperclip, StickyNote, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import LinkedNoteDialog from './LinkedNoteDialog';
import HorsePickerInline from './HorsePickerInline';
import MultiDatePicker from './MultiDatePicker';
import AttachmentPickerSheet from '../shared/AttachmentPickerSheet';
import { useConfirm } from '../../components/common/ConfirmDialog';

const SETTING_TYPES = ['Individual', 'Group'];

function localNow() {
  const now = new Date();
  return {
    date: now.toLocaleDateString('sv'),               // YYYY-MM-DD local
    time: now.toTimeString().slice(0, 5),             // HH:MM local
  };
}

export default function TurnoutLogModal({ open, onClose, onSaved, horse, locations, scheduledItem, activeLayup, availableHorses }) {
  const { date: todayDate, time: nowTime } = localNow();
  const [attachPickerOpen, setAttachPickerOpen] = useState(false);
  const confirm = useConfirm();

  const initForm = () => ({
    setting_type: scheduledItem?.setting_type || '',
    log_date: todayDate,
    start_time: scheduledItem?.preferred_time || nowTime,
    end_time: '',
    location_id: scheduledItem?.location_id || '',
    comments: '',
    publish_to_owner_portal: false,
  });

  const [form, setForm] = useState(initForm);
  const [dates, setDates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments]   = useState([]);   // { name, file } pending upload
  const [uploading, setUploading]       = useState(false);
  const [showNote, setShowNote]         = useState(false);
  const [selectedHorseIds, setSelectedHorseIds] = useState([]);

  useEffect(() => {
    if (open) {
      const { date: d, time: t } = localNow();
      setForm(initForm());
      setDates([{ id: Math.random().toString(36).slice(2), date: d, time: scheduledItem?.preferred_time || t }]);
      setAttachments([]);
      setShowNote(false);
    }
  }, [open, scheduledItem]);

  const f = async (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setAttachments(prev => [...prev, ...files.map(file => ({ name: file.name, file }))]);
    e.target.value = '';
  };

  const handleSave = async () => {
    if (!dates.length) { toast.error('Date is required'); return; }
    if (activeLayup?.restrictions?.enforce_stall_rest) {
      if (!await confirm({ title: `⚠️ ${horse.name} is on LAYUP with strict stall rest enforced. Log turnout anyway?`, destructive: true })) return;
    }
    if (Array.isArray(availableHorses) && selectedHorseIds.length === 0) { toast.error('Select at least one horse'); return; }
    setSaving(true);
    try {
      const basePayload = {
        horse_id: horse?.id,
        scheduled_care_id: scheduledItem?.id || null,
        category: 'Daily Care',
        type: 'Turnout',
        name: `Turnout${form.setting_type ? ` (${form.setting_type})` : ''}`,
        location_id: form.location_id || null,
        location_name: locations?.find(l => l.id === form.location_id)?.name || '',
        comments: [form.comments, form.end_time ? `End: ${form.end_time}` : ''].filter(Boolean).join('\n'),
        publish_to_owner_portal: form.publish_to_owner_portal,
      };

      for (const dateEntry of dates) {
        const payload = { ...basePayload, log_date: dateEntry.date, log_time: dateEntry.time || '' };
        if (Array.isArray(availableHorses)) {
          await api.post('/farmfolio/global/bulk/care-log', { horse_ids: selectedHorseIds, payload });
        } else {
          await api.post('/farmfolio/care-log', payload);
        }
      }

      if (attachments.length > 0) {
        setUploading(true);
        await Promise.allSettled(
          attachments.map(({ file }) => {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('horse_id', horse.id);
            fd.append('description', `Turnout — ${dates[0].date}${dates[0].time ? ' @ ' + dates[0].time : ''}`);
            return api.post('/files/upload', fd);
          })
        );
        setUploading(false);
      }

      if (scheduledItem?.id) {
        await api.post(`/farmfolio/scheduled-care/${scheduledItem.id}/complete`, {
          log_date: dates[0].date, log_time: dates[0].time || '',
          location_id: form.location_id || null,
          comments: basePayload.comments,
          publish_to_owner_portal: form.publish_to_owner_portal,
        }).catch(() => {});
      }

      const count = dates.length;
      toast.success(`Turnout logged${count > 1 ? ` (${count} entries)` : ''}`);
      onSaved();
      onClose();
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{scheduledItem ? 'Complete Care' : 'Quick Log Care'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {Array.isArray(availableHorses) && (
            <HorsePickerInline availableHorses={availableHorses}
              value={selectedHorseIds} onChange={setSelectedHorseIds} mode="multi" locations={locations} />
          )}
          {/* Read-only header fields */}
          <div className="rounded border px-3 py-2 text-sm bg-muted/20">
            <span className="font-semibold">Category:</span> Daily Care
          </div>
          <div className="rounded border px-3 py-2 text-sm bg-muted/20">
            <span className="font-semibold">Type:</span> Turnout
          </div>

          {/* Setting Type */}
          <div className="space-y-1">
            <Label className="font-semibold">Setting Type</Label>
            <Select value={form.setting_type || '__none__'} onValueChange={v => f('setting_type', v === '__none__' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— None —</SelectItem>
                {SETTING_TYPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Date & Time (Start) */}
          <div className="space-y-1">
            <Label className="font-semibold">Date & Start Time</Label>
            <MultiDatePicker value={dates} onChange={setDates} defaultTime={scheduledItem?.preferred_time || localNow().time} />
          </div>

          {/* End Time */}
          <div className="space-y-1">
            <Label className="font-semibold">End Time</Label>
            <Input type="time" value={form.end_time} onChange={e => f('end_time', e.target.value)}
              className={form.end_time ? 'time-filled' : ''} />
          </div>

          {/* Location */}
          <div className="space-y-1">
            <Label className="font-semibold">Location</Label>
            <Select value={form.location_id || '__none__'} onValueChange={v => f('location_id', v === '__none__' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— None —</SelectItem>
                {(locations || []).map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Comments */}
          <div className="space-y-1">
            <Label className="font-semibold">Comments</Label>
            <Textarea rows={3} value={form.comments} onChange={e => f('comments', e.target.value)}
              placeholder="Optional notes…" />
          </div>

          {/* Attachments */}
          <div className="space-y-2">
            <Button type="button" variant="outline" className="w-full justify-start gap-2"
              onClick={() => setAttachPickerOpen(true)}>
              <Paperclip className="w-4 h-4" /> Upload Attachments
            </Button>
            {attachments.length > 0 && (
              <div className="space-y-1">
                {attachments.map((a, i) => (
                  <div key={i} className="flex items-center justify-between text-xs text-muted-foreground bg-muted/30 rounded px-2 py-1">
                    <span className="truncate">{a.name}</span>
                    <Button variant="ghost" size="icon" className="h-4 w-4 shrink-0"
                      onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))}>
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Linked Note — opens full Add Note dialog as a separate popup */}
          <div>
            <Button type="button" variant="outline" className="w-full justify-start gap-2"
              onClick={() => setShowNote(true)}>
              <StickyNote className="w-4 h-4" /> Create Linked Note
            </Button>
          </div>

          {/* Publish to Owner Portal */}
          <div className="flex items-center gap-2">
            <Switch checked={form.publish_to_owner_portal} onCheckedChange={v => f('publish_to_owner_portal', v)} />
            <Label className="cursor-pointer text-sm">Publish to Owner Portal</Label>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t mt-2">
          <Button variant="outline" onClick={onClose} disabled={saving || uploading}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || uploading}
            className="bg-primary hover:bg-primary/90 text-white">
            {saving || uploading ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    <LinkedNoteDialog
      open={showNote}
      onClose={() => setShowNote(false)}
      onSaved={() => setShowNote(false)}
      horse={horse}
      careEntryRef={
        form.log_date
          ? `Turnout — ${dates[0]?.date || ''}${dates[0]?.time ? ' @ ' + dates[0].time : ''}`
          : 'Turnout care entry'
      }
    />
    <AttachmentPickerSheet
      open={attachPickerOpen}
      onClose={() => setAttachPickerOpen(false)}
      onFiles={(files) => setAttachments(prev => [...prev, ...files.map(f => ({ name: f.name, file: f }))])}
    />
  </>
  );
}
